import React, {Suspense, useEffect} from "react";
import { Route, Switch, Redirect, useLocation } from "react-router-dom";
import {useLocalStorage} from "react-use";
import {IntlProvider} from "react-intl";

import {changePassword, login, restore} from "./config/router/unauthorizedRouter";
import {authStore} from "./store/auth.store";
import {CONFIG_LS_KEY} from "./constant/key";
import {setJWT} from "./config/axiosConfig";
import {reports, root, settings, timesheet, orgApprovals, orgReports, team, teamRoles, teamPeople, admin} from "./config/router/authorizedRouter";
import PermissionGate from "./components/PermissionGate/PermissionGate";
import AuthoredProvider from "./components/AuthoredProvider/AuthoredProvider";
import LoadingPage from "./components/LoadingPage/LoadingPage";
import RecordsPage from "./components/pages/RecordsPage/RecordsPage";
import LoginPage from "./components/LoginPage/LoginPage";
import Settings from "./components/pages/Setting/Settings";
import TimesheetPage from "./components/pages/TimesheetPage/TimesheetPage";
import ApprovalsPage from "./components/pages/organization/ApprovalsPage";
import OrgReportsPage from "./components/pages/organization/ReportsPage";
import TeamPage from "./components/pages/team/TeamPage";
import RolesPage from "./components/pages/team/RolesPage";
import PeoplePage from "./components/pages/team/PeoplePage";
import languages from "./resources";
import {language} from "./enum/languages.enum";
import {themeStore} from "./store/theme.store";
import PusherComponent from "./components/PusherComponent";
import RestorePage from "./components/RestorePage/RestorePage";
import ChangePasswordPage from "./components/ChangePasswordPage/ChangePasswordPage";
import {initGA, trackPageView, setUserId} from "./utils/analytics";

const AdminPage = process.env.REACT_APP_ADMIN === 'true'
  ? React.lazy(() => import('./components/pages/AdminPage/AdminPage'))
  : null;

initGA();

function MyApp() {
  const location = useLocation();
  const locale = authStore(state => state.user?.language || language.en);
  const getCurrentUser = authStore(state => state.getCurrentUser);
  const user = authStore(state => state.user);
  const userLoading = authStore(state => state.loading.user);
  const config = authStore(state => state.config);

  const initializeTheme = themeStore(state => state.initializeTheme);

  const [storageConfig] = useLocalStorage<string | null>(
      CONFIG_LS_KEY,
      null,
      { raw: true }
  );

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (user?.username) {
      setUserId(user.username);
    }
  }, [user?.username]);

  useEffect(() => {
    initializeTheme();
  }, [initializeTheme]);

  useEffect(() => {
    if (storageConfig) {
      setJWT(JSON.parse(storageConfig).token)
    }

    void getCurrentUser();
  }, [getCurrentUser, storageConfig, config]);

  if (userLoading) {
    return <LoadingPage/>
  }

  if (user) {
    return (
      <>
        <PusherComponent storageConfig={storageConfig}/>
        <AuthoredProvider>
          <Switch>
            <Route path={reports} component={RecordsPage} />
            <Route path={timesheet} component={TimesheetPage} />
            <Route path={orgApprovals} component={ApprovalsPage} />
            <Route path={orgReports} component={OrgReportsPage} />
            {/* The more specific path has to come first — Switch takes the first match. */}
            <Route path={teamRoles} component={RolesPage} />
            <Route path={teamPeople} component={PeoplePage} />
            <Route path={team} component={TeamPage} />
            <Route path={settings} component={Settings} />
            {AdminPage && (
              <Route path={admin}>
                <PermissionGate permission="ADMIN" fallback={<Redirect to={reports} />}>
                  <Suspense fallback={<LoadingPage />}>
                    <AdminPage />
                  </Suspense>
                </PermissionGate>
              </Route>
            )}
            <Route path={root}>
              <Redirect to={reports}/>
            </Route>
          </Switch>
        </AuthoredProvider>
      </>
    )
  }

  return (
    <IntlProvider
      messages={languages[locale]}
      locale={language[locale]}
    >
      <Switch>
        <Route path={login} component={LoginPage}/>
        <Route path={restore} component={RestorePage}/>
        <Route path={changePassword} component={ChangePasswordPage}/>
        <Route path={root} >
          <Redirect to={login} />
        </Route>
      </Switch>
    </IntlProvider>
  );
}

export default MyApp;
